/**
 * Shipping API Service - Backend Custom API Client
 *
 * Service para interactuar con los endpoints de envíos del Backend Custom API.
 * Este servicio NO usa Supabase directo, sino que consume los endpoints REST
 * del backend Node.js que están documentados en openapi.yaml.
 *
 * **Endpoints soportados:**
 * - POST /api/shipping/calculate-cost - Calcular costo de envío
 * - POST /api/shipping/create-shipment - Crear envío en MercadoEnvíos
 * - GET /api/shipping/orders/{order_id}/label - Descargar etiqueta de envío (PDF)
 * - GET /api/shipping/tracking/{shipment_id} - Consultar seguimiento
 * - GET /api/shipping/zones - Obtener zonas de envío disponibles
 *
 * **Integración con MercadoEnvíos (Chile):**
 * Este servicio se integra con MercadoEnvíos para cálculo de tarifas,
 * creación de envíos y seguimiento. El backend maneja:
 * - Validación de direcciones chilenas
 * - Consulta de tarifas en tiempo real
 * - Creación de envíos con etiquetas
 * - Cache de tarifas frecuentes
 *
 * @module services/shippingApiService
 *
 * @example
 * import { shippingApiService } from '@/services/shippingApiService';
 *
 * // Calcular costo de envío
 * const { data, error } = await shippingApiService.calculateShippingCost({
 *   artwork_id: 'artwork-uuid',
 *   destination_city_id: 'CLI_0001',
 *   shipping_type: 'standard'
 * });
 */

import { apiClient } from '@/utils/apiClient';

/**
 * Shipping API Service class
 */
class ShippingApiService {
  /**
   * Calcular costo de envío para un artwork a una ciudad destino
   *
   * **Flujo Backend:**
   * 1. Obtiene dimensiones y peso del artwork desde BD
   * 2. Consulta tarifas de MercadoEnvíos API
   * 3. Aplica cache para optimizar consultas frecuentes (15 min TTL)
   * 4. Retorna costo estimado y tiempo de entrega
   *
   * **Casos especiales:**
   * - Si MercadoEnvíos no está disponible, usa tarifa de fallback
   * - Si el artwork no tiene dimensiones, usa valores default
   * - Si el destino no está cubierto, retorna available: false
   *
   * **Endpoint:** POST /api/shipping/calculate-cost
   *
   * @param {Object} params - Parámetros para calcular envío
   * @param {string} params.artwork_id - UUID del artwork a enviar (required)
   * @param {string} params.destination_city_id - ID de ciudad destino MercadoEnvíos (required, ej: "CLI_0001")
   * @param {string} [params.shipping_type='standard'] - Tipo de envío: 'standard' | 'express'
   *
   * @returns {Promise<{success: boolean, data?: Object, error?: string}>}
   * @returns {Object} data - Resultado del cálculo
   * @returns {number} data.cost - Costo del envío en CLP
   * @returns {string} data.estimated_days - Tiempo estimado (ej: "3-5 días")
   * @returns {boolean} data.available - Si el envío está disponible
   * @returns {string} data.service_type - Tipo de servicio utilizado
   * @returns {string} data.currency - Moneda (CLP)
   * @returns {boolean} data.cached - Si proviene del cache
   * @returns {boolean} data.fallback - Si se usó tarifa de fallback
   *
   * @example
   * // Calcular envío standard
   * const { success, data, error } = await shippingApiService.calculateShippingCost({
   *   artwork_id: '550e8400-e29b-41d4-a716-446655440001',
   *   destination_city_id: 'CLI_0001', // Santiago
   *   shipping_type: 'standard'
   * });
   *
   * if (success) {
   *   console.log('Costo:', data.cost, 'CLP');
   *   console.log('Entrega:', data.estimated_days);
   *   console.log('Disponible:', data.available);
   * } else {
   *   console.error('Error:', error);
   * }
   *
   * @example
   * // Calcular envío express
   * const { success, data } = await shippingApiService.calculateShippingCost({
   *   artwork_id: '550e8400-e29b-41d4-a716-446655440001',
   *   destination_city_id: 'CLI_0002', // Valparaíso
   *   shipping_type: 'express'
   * });
   */
  async calculateShippingCost({ artwork_id, destination_county_code, shipping_type = 'standard' }) {
    return await apiClient.post('/api/shipping/calculate-cost', {
      artwork_id,
      destination_county_code,
      shipping_type,
    });
  }

  /**
   * Crear envío en MercadoEnvíos para una orden
   *
   * **Restricciones:**
   * - Solo el artista/vendedor puede crear envíos
   * - La orden debe estar en estado 'processing' o 'approved'
   * - Requiere dirección de recogida válida (dirección chilena)
   *
   * **Flujo Backend:**
   * 1. Valida permisos (usuario es el vendedor de la orden)
   * 2. Valida estado de la orden (processing/approved)
   * 3. Valida y normaliza dirección de recogida (formato chileno)
   * 4. Crea envío en MercadoEnvíos API
   * 5. Actualiza la orden con información de tracking
   * 6. Retorna datos del envío creado (tracking, etiqueta, etc.)
   *
   * **Dirección de Recogida:**
   * Debe incluir: address, city, city_id, state, zip_code
   * El backend valida que city_id sea válido en MercadoEnvíos
   *
   * **Endpoint:** POST /api/shipping/create-shipment
   *
   * @param {Object} params - Parámetros para crear envío
   * @param {string} params.order_id - UUID de la orden a enviar (required)
   * @param {Object} params.pickup_address - Dirección de recogida (required)
   * @param {string} params.pickup_address.address - Calle (ej: "Av. Providencia 1234")
   * @param {string} params.pickup_address.city - Ciudad (ej: "Santiago")
   * @param {string} params.pickup_address.city_id - ID ciudad MercadoEnvíos (ej: "CLI_0001")
   * @param {string} params.pickup_address.state - Región (ej: "RM")
   * @param {string} params.pickup_address.zip_code - Código postal (ej: "7500000")
   *
   * @returns {Promise<{success: boolean, data?: Object, error?: string}>}
   * @returns {Object} data - Resultado de creación
   * @returns {Object} data.shipment - Datos del envío creado
   * @returns {string} data.shipment.id - ID del envío en MercadoEnvíos
   * @returns {string} data.shipment.tracking_url - URL de seguimiento público
   * @returns {string} data.shipment.tracking_number - Número de seguimiento
   * @returns {string} data.shipment.label_url - URL de la etiqueta de envío (PDF)
   * @returns {string} data.shipment.estimated_delivery - Fecha estimada de entrega (ISO 8601)
   * @returns {string} data.shipment.status - Estado: 'pending' | 'ready_to_ship' | 'shipped' | 'delivered'
   *
   * @example
   * // Crear envío para orden
   * const { success, data, error } = await shippingApiService.createShipment({
   *   order_id: '550e8400-e29b-41d4-a716-446655440001',
   *   pickup_address: {
   *     address: 'Av. Providencia 1234',
   *     city: 'Santiago',
   *     city_id: 'CLI_0001',
   *     state: 'RM',
   *     zip_code: '7500000'
   *   }
   * });
   *
   * if (success) {
   *   console.log('Envío creado:', data.shipment.id);
   *   console.log('Tracking:', data.shipment.tracking_number);
   *   console.log('Etiqueta:', data.shipment.label_url);
   * } else {
   *   console.error('Error:', error);
   * }
   *
   * @example
   * // Manejo de errores
   * const result = await shippingApiService.createShipment({
   *   order_id: 'order-id',
   *   pickup_address: { ... }
   * });
   *
   * if (!result.success) {
   *   if (result.error.includes('403')) {
   *     console.error('Sin permisos para crear envío');
   *   } else if (result.error.includes('INVALID_ORDER')) {
   *     console.error('Orden en estado incorrecto');
   *   }
   * }
   */
  async createShipment({ order_id, pickup_address }) {
    return await apiClient.post('/api/shipping/create-shipment', {
      order_id,
      pickup_address,
    });
  }

  /**
   * Descargar etiqueta de envío en PDF para una orden
   *
   * **Restricciones:**
   * - Solo el vendedor de la orden puede descargar la etiqueta
   * - La orden debe tener un envío creado
   * - Retorna PDF binario para imprimir
   *
   * **Flujo Backend:**
   * 1. Valida permisos (usuario es el vendedor de la orden)
   * 2. Verifica que la orden tiene envío creado
   * 3. Obtiene etiqueta desde MercadoEnvíos API
   * 4. Retorna PDF binario con header Content-Disposition
   *
   * **Endpoint:** GET /api/shipping/orders/{order_id}/label
   *
   * @param {string} order_id - UUID de la orden (required)
   *
   * @returns {Promise<{success: boolean, data?: Blob, error?: string}>}
   * @returns {Blob} data - PDF binario de la etiqueta
   *
   * @example
   * // Descargar y guardar etiqueta
   * const { success, data, error } = await shippingApiService.getShippingLabel('order-uuid');
   *
   * if (success) {
   *   const blob = data;
   *   const url = URL.createObjectURL(blob);
   *   const a = document.createElement('a');
   *   a.href = url;
   *   a.download = `shipping-label-${order_id}.pdf`;
   *   a.click();
   *   URL.revokeObjectURL(url);
   * } else {
   *   console.error('Error:', error);
   * }
   */
  async getShippingLabel(order_id) {
    return await apiClient.get(`/api/shipping/orders/${order_id}/label`, {
      responseType: 'blob', // Para recibir PDF binario
    });
  }

  /**
   * Consultar seguimiento de envío
   *
   * **Información incluida:**
   * - Estado actual del envío
   * - Ubicación actual
   * - Historial de eventos de seguimiento
   * - Fecha estimada de entrega actualizada
   *
   * **Flujo Backend:**
   * 1. Consulta estado actualizado en MercadoEnvíos API
   * 2. Retorna historial de eventos (públicos + internos)
   * 3. Incluye ubicación actual y próximos pasos
   *
   * **Endpoint:** GET /api/shipping/tracking/{shipment_id}
   *
   * @param {string} shipment_id - ID del envío en MercadoEnvíos (required, min 5 chars)
   *
   * @returns {Promise<{success: boolean, data?: Object, error?: string}>}
   * @returns {Object} data - Información de seguimiento
   * @returns {Object} data.tracking - Datos de seguimiento
   * @returns {string} data.tracking.status - Estado actual: 'pending' | 'ready_to_ship' | 'shipped' | 'in_transit' | 'out_for_delivery' | 'delivered' | 'failed_attempt' | 'returned'
   * @returns {string} data.tracking.tracking_number - Número de seguimiento
   * @returns {string} data.tracking.tracking_url - URL de seguimiento público
   * @returns {string} data.tracking.estimated_delivery - Fecha estimada (ISO 8601)
   * @returns {string} data.tracking.current_location - Ubicación actual
   * @returns {Array} data.tracking.events - Eventos de seguimiento públicos
   * @returns {Array} data.tracking.internal_events - Eventos internos detallados
   *
   * @example
   * // Consultar seguimiento
   * const { success, data, error } = await shippingApiService.getTrackingInfo('ME123456789');
   *
   * if (success) {
   *   console.log('Estado:', data.tracking.status);
   *   console.log('Ubicación:', data.tracking.current_location);
   *   console.log('Eventos:', data.tracking.events.length);
   *
   *   data.tracking.events.forEach(event => {
   *     console.log(`${event.timestamp}: ${event.description} (${event.location})`);
   *   });
   * } else {
   *   console.error('Error:', error);
   * }
   */
  async getTrackingInfo(shipment_id) {
    return await apiClient.get(`/api/shipping/tracking/${shipment_id}`);
  }

  /**
   * Obtener zonas de envío disponibles
   *
   * **Información incluida:**
   * - Regiones y ciudades cubiertas
   * - Tarifas base por zona
   * - Tiempos de entrega estimados
   * - Estados activos/inactivos
   *
   * **Flujo Backend:**
   * 1. Obtiene zonas configuradas de la BD
   * 2. Filtra zonas activas
   * 3. Incluye tarifas base y tiempos de entrega
   *
   * **Endpoint:** GET /api/shipping/zones
   *
   * @returns {Promise<{success: boolean, data?: Object, error?: string}>}
   * @returns {Object} data - Zonas de envío
   * @returns {Array} data.zones - Lista de zonas disponibles
   *
   * @example
   * // Obtener zonas de envío
   * const { success, data, error } = await shippingApiService.getShippingZones();
   *
   * if (success) {
   *   data.zones.forEach(zone => {
   *     console.log(`${zone.name}: ${zone.base_rate} CLP (${zone.estimated_days} días)`);
   *   });
   * } else {
   *   console.error('Error:', error);
   * }
   */
  async getShippingZones() {
    return await apiClient.get('/api/shipping/zones');
  }
}

export const shippingApiService = new ShippingApiService();
